import { ChevronDown, FilterIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListMyOffers } from '@cold-ambulance/client';
import {
  Button,
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cold-ambulance/shadcn';
import { useData } from '@cold-ambulance/ui';

import { OfferCard, OfferCardSkeleton } from './offer-card.tsx';

export default function OffersPage() {
  const { t } = useTranslation();
  const offersQuery = useData('GET /me/offers', {
    pageSize: 100,
  });

  const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>([]);

  const filteredRecords = useMemo(() => {
    const records = offersQuery.data?.records ?? [];
    if (selectedStatuses.length === 0) return records;
    return records.filter((r) => selectedStatuses.includes(r.status));
  }, [offersQuery.data?.records, selectedStatuses]);

  return (
    <div className="page-container">
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t('offers.sentOffers')}</h1>
          <Filter value={selectedStatuses} onApply={setSelectedStatuses} />
        </div>

        {offersQuery.isLoading ? (
          <div className="mt-8 grid grid-cols-1 gap-4">
            <OfferCardSkeleton />
            <OfferCardSkeleton />
            <OfferCardSkeleton />
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4">
            {filteredRecords.map((it) => (
              <OfferCard key={it.id} offer={it} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type StatusOption = ListMyOffers['records'][number]['status'];

export function Filter({
  value,
  onApply,
}: {
  value: StatusOption[];
  onApply: (value: StatusOption[]) => void;
}) {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState<StatusOption[]>(value);

  const OPTIONS: StatusOption[] = [
    'PENDING',
    'ACCEPTED',
    'IN_PROGRESS',
    'NOT_SELECTED',
    'CANCELED',
  ];
  const LABELS: Record<StatusOption, string> = {
    PENDING: t('offers.pending'),
    ACCEPTED: t('offers.accepted'),
    IN_PROGRESS: t('offers.inProgress'),
    NOT_SELECTED: t('offers.notSelected'),
    CANCELED: t('offers.canceled'),
  };

  const allChecked = localValue.length === OPTIONS.length;

  const handleToggleAll = (checked: boolean | string) => {
    if (checked === true) {
      setLocalValue(OPTIONS);
      return;
    }
    setLocalValue([]);
  };

  const handleToggle = (opt: StatusOption, checked: boolean | string) => {
    setLocalValue((prev) => {
      if (checked === true) {
        if (prev.includes(opt)) return prev;
        return [...prev, opt];
      }
      return prev.filter((v) => v !== opt);
    });
  };

  const summaryLabel =
    allChecked || localValue.length === 0
      ? t('offers.all')
      : localValue.map((s) => LABELS[s]).join(', ');

  return (
    <Popover>
      <PopoverTrigger className="flex min-w-3xs items-center gap-x-2 rounded-md border px-4">
        <FilterIcon size={16} />
        <p className="text-sm">{t('offers.status')}: {summaryLabel}</p>
        <ChevronDown size={20} className="ms-auto" />
      </PopoverTrigger>
      <PopoverContent className="w-3xs space-y-2.5">
        <div className="flex w-full items-center gap-x-2">
          <Checkbox
            checked={allChecked}
            onCheckedChange={handleToggleAll}
            aria-label={t('common.viewAll')}
          />
          <Label>{t('offers.all')}</Label>
        </div>
        {OPTIONS.map((opt) => (
          <div key={opt} className="flex w-full items-center gap-x-2">
            <Checkbox
              checked={localValue.includes(opt)}
              onCheckedChange={(c) => handleToggle(opt, c)}
              aria-label={`${t('offers.status')} ${LABELS[opt]}`}
            />
            <Label>{LABELS[opt]}</Label>
          </div>
        ))}
        <Button
          size={'sm'}
          className="w-full"
          onClick={() => onApply(localValue)}
        >
          {t('offers.filterResults')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
